/**
 * Section config per medical path for the universal "Springen zu" nav.
 * Each entry: path (or path prefix) -> array of { id, label }.
 * id must match an element id in the page/modal content.
 */

import { HC_SERVICES } from "./hcServicesData";
import { getInsightBySlug } from "./insightsData";

const PATH_TO_SERVICE_ID = {
  "/medical/suchmethode": "suchmethode",
  "/medical/executive-search": "executive-search",
  "/medical/praxisnachfolger-suche": "praxisnachfolger",
};

function buildServicePageSections(service) {
  if (!service?.detail) return [];
  const prefix = "service-section";
  const out = [];
  service.detail.sections?.forEach((s, i) => {
    out.push({
      id: `${prefix}-${i}`,
      label: s.shortLabel || (s.heading && s.heading.split(/\s+/).slice(0, 2).join(" ")) || `Abschnitt ${i + 1}`,
    });
  });
  if (service.detail.cta) {
    out.push({
      id: `${prefix}-cta`,
      label: service.detail.cta.shortLabel || "Jetzt anfragen",
    });
  }
  if (service.detail.bottomImage) {
    out.push({
      id: `${prefix}-bottom-image`,
      label: service.detail.bottomImage.shortLabel || "Bild",
    });
  }
  if (service.detail.bottomImages?.images?.length) {
    out.push({
      id: `${prefix}-bottom-images`,
      label: service.detail.bottomImages.shortLabel || "Bilder",
    });
  }
  if (service.detail.showContactForm) {
    out.push({ id: `${prefix}-contact`, label: "Kontakt" });
  }
  return out;
}

function buildInsightDetailSections(article) {
  if (!article?.detail) return [];
  const prefix = "insight-section";
  const out = [];
  article.detail.sections?.forEach((s, i) => {
    const label =
      s.shortLabel ||
      (s.heading && s.heading.split(/\s+/).slice(0, 3).join(" ")) ||
      `Abschnitt ${i + 1}`;
    out.push({ id: `${prefix}-${i}`, label });
  });
  if (article.detail.cta) {
    out.push({ id: `${prefix}-cta`, label: "Kontakt" });
  }
  if (article.detail.bottomImage) {
    out.push({ id: `${prefix}-bottom-image`, label: "Bild" });
  }
  return out;
}

const HOME_SECTIONS = [
  { id: "section-hero", label: "Start" },
  { id: "section-services", label: "Dienstleistungen" },
  { id: "section-about", label: "Über uns" },
  { id: "section-cases", label: "Kundenarbeit" },
  { id: "section-team", label: "Team" },
  { id: "section-insights", label: "Erkenntnisse" },
  { id: "section-stats-logos", label: "Partner & Zahlen" },
  { id: "section-explore", label: "Entdecken" },
];

const PATH_SECTIONS = {
  "/medical": HOME_SECTIONS,
  "/medical/": HOME_SECTIONS,
  "/medical/geschichte": [
    { id: "section-geschichte-gruender", label: "Der Gründer" },
    { id: "section-geschichte-marke", label: "Wachstum & Marke" },
    { id: "section-geschichte-heute", label: "Heute" },
  ],
  "/medical/team": [{ id: "section-team", label: "Team" }],
  "/medical/besetzte-positionen": [
    { id: "section-besetzte-positionen", label: "Besetzte Positionen" },
  ],
  "/medical/auszeichnungen": [
    { id: "section-auszeichnungen", label: "Auszeichnungen" },
  ],
  "/medical/referenzen": [
    { id: "section-erfolgsgeschichten", label: "Referenzen" },
  ],
  "/medical/garantien": [{ id: "section-garantien", label: "Garantien" }],
  "/medical/soziale-verantwortung": [
    { id: "section-soziale-verantwortung", label: "Soziale Verantwortung" },
  ],
  "/medical/rekrutierungsprozess": [
    { id: "section-rekrutierungsprozess", label: "Rekrutierungsprozess" },
  ],
  "/medical/faq": [{ id: "section-faq", label: "FAQ" }],
  "/medical/kandidatenreise": [
    { id: "section-kandidatenreise-intro", label: "Überblick" },
    { id: "section-kandidatenreise-1", label: "Erster Kontakt" },
    { id: "section-kandidatenreise-2", label: "Profil" },
    { id: "section-kandidatenreise-3", label: "Mandate" },
    { id: "section-kandidatenreise-4", label: "Begleitung" },
    { id: "section-kandidatenreise-5", label: "Einstieg" },
    { id: "section-kandidatenreise-cta", label: "Kontakt" },
  ],
  "/medical/karriere": [
    { id: "section-karriere-beratung", label: "Karriereberatung" },
    { id: "section-karriere-stimmen", label: "Kandidatenstimmen" },
    { id: "section-karriere-click", label: "Karriereclick" },
    { id: "section-karriere-kontakt", label: "Kontakt" },
  ],
  "/medical/kandidatenfeedback": [
    { id: "section-kandidatenfeedback", label: "Kandidatenfeedback" },
  ],
  "/medical/faq-kandidaten": [
    { id: "section-faq-kandidaten", label: "FAQ für Kandidaten" },
  ],
  "/medical/insights": [{ id: "section-insights", label: "Einblicke" }],
  "/medical/fallstudien": [
    { id: "section-fallstudien", label: "Fallstudien" },
  ],
  "/medical/impressum": [
    { id: "section-impressum-unternehmen", label: "Unternehmen" },
    { id: "section-impressum-gesellschafter", label: "Gesellschafter" },
    { id: "section-impressum-rechtliches", label: "Rechtliches" },
    { id: "section-impressum-bildquellen", label: "Bildquellen" },
    { id: "section-impressum-contact", label: "Kontakt" },
  ],
  "/medical/agb": [
    { id: "section-agb-intro", label: "Anbieter" },
    { id: "section-agb-1", label: "§ 1 Vertragsgegenstand" },
    { id: "section-agb-2", label: "§ 2 Leistung" },
    { id: "section-agb-3", label: "§ 3 Zusammenarbeit" },
    { id: "section-agb-4", label: "§ 4 Beauftragung" },
    { id: "section-agb-5", label: "§ 5 Honorar" },
    { id: "section-agb-6", label: "§ 6 Laufzeit" },
    { id: "section-agb-7", label: "§ 7 Garantie" },
    { id: "section-agb-8", label: "§ 8 Geistiges Eigentum" },
    { id: "section-agb-9", label: "§ 9 Sonstiges" },
    { id: "section-agb-10", label: "§ 10 Datenschutz" },
    { id: "section-agb-11", label: "§ 11 Schlussbestimmungen" },
    { id: "section-agb-contact", label: "Kontakt" },
  ],
  "/medical/datenschutz": [
    { id: "section-datenschutz-ueberblick", label: "Überblick" },
    { id: "section-datenschutz-pflicht", label: "Pflichtinformationen" },
    { id: "section-datenschutz-cookies", label: "Cookies" },
    { id: "section-datenschutz-kontakt", label: "Kontaktformular" },
    { id: "section-datenschutz-sozial", label: "Soziale Medien" },
    { id: "section-datenschutz-analyse", label: "Analyse & Werbung" },
    { id: "section-datenschutz-plugins", label: "Plugins & Tools" },
    { id: "section-datenschutz-contact", label: "Kontakt" },
  ],
};

export function getSectionsForPath(pathname) {
  if (!pathname || !pathname.startsWith("/medical")) return [];
  const serviceId = PATH_TO_SERVICE_ID[pathname];
  if (serviceId) {
    const service = HC_SERVICES.find((s) => s.id === serviceId);
    const built = buildServicePageSections(service);
    if (built.length) return built;
  }
  const exact = PATH_SECTIONS[pathname];
  if (exact) return exact;
  if (pathname.startsWith("/medical/insights/")) {
    const slug = pathname.replace(/^\/medical\/insights\/?/, "").split("/")[0];
    const article = getInsightBySlug(slug);
    const built = buildInsightDetailSections(article);
    if (built.length) return built;
    return [{ id: "section-insight-detail", label: "Inhalt" }];
  }
  return [];
}
